// /shelves: the 16:45 count, raw materials from inventory, the shelf ledger,
// and which e-paper tag shows which shelf.
import { useIonToast } from "@ionic/react";
import { useMemo, useState } from "react";
import type { RawRow, ShelfRow, StockEventRow } from "../lib/types";
import { ShelfGauge } from "../design";
import { announce, can, useApi, useMe } from "../api/live";
import { ApiError, post } from "../api/client";
import { paths } from "../api/paths";
import { dayMonth, stamp } from "../lib/days";
import { shelfSentence } from "../lib/copy";
import { useNow } from "../lib/now";
import { Page } from "../shell/Page";
import { ErrorNote, Loading } from "../shell/ui";

type WithTag = ShelfRow & { eslMac?: string | null };

export function ShelvesPage() {
  const { me } = useMe();
  const now = useNow();
  const shelves = useApi<ShelfRow[]>(paths.shelves(), ["stock", "shelf", "session"]);
  const raw = useApi<RawRow[]>(paths.raw(), ["stock"]);
  const ledger = useApi<StockEventRow[]>(paths.stockEvents(), ["stock", "session"]);
  const [tab, setTab] = useState<"count" | "raw" | "ledger" | "tags">("count");
  const lines = useMemo(() => {
    const m = new Map<string, ShelfRow[]>();
    for (const s of shelves.data ?? []) m.set(s.line ?? "Other", [...(m.get(s.line ?? "Other") ?? []), s]);
    return [...m.entries()];
  }, [shelves.data]);

  const red = (shelves.data ?? []).filter((s) => s.state === "red" && !s.isOutput);
  return (
    <Page title="Shelves">
      <div className="page__head">
        <h1>Shelves</h1><span className="spacer" />
        <div className="tabs" role="tablist">
          {(["count", "raw", "ledger", "tags"] as const).map((t) => (
            <button key={t} type="button" role="tab" aria-selected={tab === t} className={`btn btn--sm${tab === t ? " btn--primary" : ""}`} onClick={() => setTab(t)}>
              {{ count: "16:45 count", raw: "Raw materials", ledger: "Ledger", tags: "Tags" }[t]}
            </button>
          ))}
        </div>
      </div>
      {!shelves.data ? (shelves.error ? <ErrorNote error={shelves.error} retry={shelves.reload} /> : <Loading />) : tab === "count" ? (
        <div className="stack">
          {red.length ? <p className="error-card">Below Critical, so fill today: {red.map((s) => `${s.code} ${s.name} ${s.level.qty}`).join(" · ")}</p> : null}
          <CountForm lines={lines} stockFor={now.day} canCount={can(me, "supervisor")} />
        </div>
      ) : tab === "raw" ? <RawTable rows={raw.data} error={raw.error} />
        : tab === "ledger" ? <Ledger rows={ledger.data} shelves={shelves.data} />
        : <Tags shelves={shelves.data as WithTag[]} />}
    </Page>
  );
}

function CountForm({ lines, stockFor, canCount }: { lines: [string, ShelfRow[]][]; stockFor: string; canCount: boolean }) {
  const [present] = useIonToast();
  const [counts, setCounts] = useState<Record<string, string | boolean>>({});
  const [busy, setBusy] = useState(false);
  const entered = Object.keys(counts).length;
  const save = async () => {
    setBusy(true);
    try {
      await post(paths.counts(), { stockFor, counts: Object.entries(counts).map(([shelf, v]) => (typeof v === "boolean" ? { shelf, enough: v } : { shelf, qty: v === "" ? null : Number(v) })) });
      present({ message: `Counted ${entered} shelves for ${dayMonth(stockFor)}`, duration: 2500, color: "success" });
      setCounts({});
      announce(["stock"]);
    } catch (e) { present({ message: (e as ApiError).message, duration: 4000, color: "danger" }); }
    setBusy(false);
  };
  return (
    <section className="card">
      <div className="card__head"><h2>Tonight's count, {dayMonth(stockFor)}</h2><span className="small muted">The faint number is the last count.</span></div>
      <div className="counts">
        {lines.map(([line, rows]) => (
          <div key={line} className="counts__line">
            <span className="oc-label">{line}</span>
            <div className="counts__rows">
            {rows.map((s) => (
              <div key={s.code} className="counts__row">
                <ShelfGauge shelf={s} showFlow />
                {s.countMode === "enough" ? (
                  <div className="choices" role="group" aria-label={`${s.name}: enough?`}>
                    {[true, false].map((v) => <button key={String(v)} type="button" className="choice" disabled={!canCount} aria-pressed={counts[s.code] === v} onClick={() => setCounts({ ...counts, [s.code]: v })}>{v ? "Enough" : "Not enough"}</button>)}
                  </div>
                ) : (
                  <input className="input input--num" inputMode="numeric" aria-label={`${s.code} ${s.name} count`} disabled={!canCount}
                    placeholder={s.level.qty != null ? String(s.level.qty) : "–"} value={(counts[s.code] as string) ?? ""}
                    onChange={(e) => setCounts({ ...counts, [s.code]: e.target.value.replace(/\D/g, "") })} />
                )}
              </div>
            ))}
            </div>
          </div>
        ))}
      </div>
      <div className="row" style={{ justifyContent: "flex-end", marginTop: "var(--space-4)" }}>
        {!canCount ? <span className="small muted">Supervisors count the shelves.</span> : null}
        <button type="button" className="btn btn--primary" disabled={!entered || busy || !canCount} onClick={save}>{entered ? `Save ${entered} count${entered === 1 ? "" : "s"}` : "Save the count"}</button>
      </div>
    </section>
  );
}

function RawTable({ rows, error }: { rows: RawRow[] | null; error: ApiError | null }) {
  if (!rows) return error ? <ErrorNote error={error} /> : <Loading />;
  return (
    <section className="card scroll-x">
      <div className="card__head"><h2>Raw materials</h2><span className="small muted">Read live from the inventory</span></div>
      <table className="table">
        <thead><tr><th>SKU</th><th>What</th><th className="num">In stock</th><th className="num">Min</th><th>As of</th></tr></thead>
        <tbody>{rows.map((r) => (
          <tr key={r.sku}><td><b>{r.sku}</b></td><td>{r.description}</td>
            <td className="num"><span className={`pill ${r.stock != null && r.min != null && r.stock < r.min ? "pill--r" : "pill--muted"}`}>{r.stock ?? "–"}</span></td>
            <td className="num">{r.min ?? "–"}</td><td className="muted">{stamp(r.asOf)}</td></tr>
        ))}</tbody>
      </table>
    </section>
  );
}

function Ledger({ rows, shelves }: { rows: StockEventRow[] | null; shelves: ShelfRow[] }) {
  const [shelf, setShelf] = useState("");
  if (!rows) return <Loading />;
  const shown = rows.filter((r) => !shelf || r.shelf === shelf).slice(0, 80);
  return (
    <section className="card scroll-x">
      <div className="card__head"><h2>Shelf ledger</h2>
        <select className="select select--inline" aria-label="Shelf" value={shelf} onChange={(e) => setShelf(e.target.value)}>
          <option value="">Every shelf</option>{shelves.map((s) => <option key={s.code} value={s.code}>{s.code} {s.name}</option>)}
        </select></div>
      <p className="small muted" style={{ marginBottom: "var(--space-3)" }}>A level is the last count plus the moves since. The source says where a line came from: a count, a logged session, or (later) a scanner.</p>
      <table className="table">
        <thead><tr><th>When</th><th>Shelf</th><th>What</th><th className="num">Qty</th><th>Source</th><th>Who</th><th>Note</th></tr></thead>
        <tbody>{shown.map((r) => (
          <tr key={r.id}><td className="muted">{stamp(r.at)}</td><td><b>{r.shelf ?? r.sku}</b></td><td>{r.kind}</td>
            <td className="num">{r.qty != null ? (r.kind === "move" && r.qty > 0 ? `+${r.qty}` : r.qty) : r.enough == null ? "–" : r.enough ? "enough" : "not enough"}</td>
            <td>{r.source}</td><td>{r.by ?? ""}</td><td className="muted">{r.note ?? ""}</td></tr>
        ))}</tbody>
      </table>
    </section>
  );
}

function Tags({ shelves }: { shelves: WithTag[] }) {
  return (
    <section className="card scroll-x">
      <div className="card__head"><h2>Which tag shows which shelf</h2><span className="small muted">Preview: no e-paper pushes</span></div>
      <table className="table">
        <thead><tr><th>Shelf</th><th>Floor</th><th>Tag</th><th>Shows</th></tr></thead>
        <tbody>{shelves.filter((s) => !s.isOutput).map((s) => (
          <tr key={s.code}><td><b>{s.code}</b> {s.name}</td><td>{s.floor ?? ""}</td>
            <td>{s.eslMac ? <code>{s.eslMac}</code> : <span className="muted">No tag yet</span>}</td>
            <td className="muted">{shelfSentence(s)}</td></tr>
        ))}</tbody>
      </table>
    </section>
  );
}
